import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useParams,
  Outlet,
  useLocation,
} from "react-router-dom";
import {
  LocaleProvider,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  useLocale,
  getBrowserLocale,
} from "./contexts/LocaleContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { HelmetProvider } from "react-helmet-async";
import InvoiceApp from "./InvoiceApp";
import FakturamaLanding from "./LandingPage";
import InvoiceList from "./InvoiceList";
import Statistics from "./Statistics";
import InvoiceTemplates from "./InvoiceTemplates";
import RepeatingInvoices from "./RepeatingInvoices";
import TimeTracking from "./TimeTracking";
import ClientNotifications from "./ClientNotifications";
import TransferInvoices from "./TransferInvoices";
import Layout from "./Layout";
import NotFound from "./NotFound";
import LogoutRoute from "./LogoutRoute";
import ProtectedRoute from "./ProtectedRoute";
import TermsOfService from "./TermsOfService";
import PrivacyPolicy from "./PrivacyPolicy";
import UserSettings from "./UserSettings";
import VerifyEmail from "./VerifyEmail";
import ResetPassword from "./ResetPassword";
import Blog from "./Blog";
import RateLimitModal from "./components/RateLimitModal";
import SharedInvoiceViewer from "./SharedInvoiceViewer";
import Expenses from "./Expenses";
import DevTools from "./DevTools";
import UnsubscribeSuccess from "./UnsubscribeSuccess";

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Helper to track custom events
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
};

// Google Analytics tracking component
function TrackPageViews() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("config", "G-YTWZFZL4VW", {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
}

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    // If URL contains an explicit hash, let the browser handle it
    if (location.hash) return;

    // Use rAF to run after React has committed the new screen
    requestAnimationFrame(() => {
      try {
        // Covers most cases (when window is the scroll container)
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch {
        // ignore
      }

      // Also reset common scroll roots for cases where app uses a nested scroll container
      try {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const root = document.getElementById("root");
        if (root) root.scrollTop = 0;
      } catch {
        // ignore
      }
    });
  }, [location.pathname, location.hash]);

  return null;
}

// Language redirect component for the root path /
function RootRedirect() {
  // Мы убрали автоматический редирект по языку браузера,
  // чтобы корень сайта всегда оставался доступным (дефолтная чешская версия).
  return <FakturamaLanding />;
}

export default function AppRouter() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <LocaleProvider>
            <TrackPageViews />
            <ScrollToTopOnRouteChange />
            <RateLimitModal />
            <Routes>
              {/* Root route with auto-detection */}
              <Route path="/" element={<RootRedirect />}>
                <Route path="login" element={<></>} />
                <Route path="signup" element={<></>} />
                <Route path="forgot-password" element={<></>} />
              </Route>

              {/* Specific public routes ABOVE the locale parameter */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:postSlug" element={<Blog />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unsubscribe" element={<UnsubscribeSuccess />} />
              <Route path="/v/:token" element={<SharedInvoiceViewer />} />

              {/* Protected routes without prefix */}
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/app" element={<InvoiceApp />} />
                <Route path="/invoice-list" element={<InvoiceList />} />
                <Route path="/payment-success" element={<InvoiceList />} />
                <Route path="/statistics" element={<Statistics />} />
                <Route
                  path="/transfer-invoices"
                  element={<TransferInvoices />}
                />
                <Route
                  path="/repeating-invoices"
                  element={<RepeatingInvoices />}
                />
                <Route path="/time-tracking" element={<TimeTracking />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route
                  path="/client-notifications"
                  element={<ClientNotifications />}
                />
                <Route path="/settings" element={<UserSettings />} />
                {process.env.NODE_ENV === "development" && (
                  <Route path="/devtools" element={<DevTools />} />
                )}
              </Route>

              {/* Locale-prefixed routes (LAST to avoid catching specific routes as :lang) */}
              <Route path="/:lang">
                <Route index element={<FakturamaLanding />} />
                <Route path="login" element={<FakturamaLanding />} />
                <Route path="signup" element={<FakturamaLanding />} />
                <Route path="forgot-password" element={<FakturamaLanding />} />
                <Route path="blog" element={<Blog />} />
                <Route path="blog/:postSlug" element={<Blog />} />
                <Route path="terms" element={<TermsOfService />} />
                <Route path="privacy" element={<PrivacyPolicy />} />
                <Route path="verify-email" element={<VerifyEmail />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="unsubscribe" element={<UnsubscribeSuccess />} />
                <Route path="v/:token" element={<SharedInvoiceViewer />} />

                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="app" element={<InvoiceApp />} />
                  <Route path="invoice-list" element={<InvoiceList />} />
                  <Route path="payment-success" element={<InvoiceList />} />
                  <Route path="statistics" element={<Statistics />} />
                  <Route
                    path="transfer-invoices"
                    element={<TransferInvoices />}
                  />
                  <Route
                    path="repeating-invoices"
                    element={<RepeatingInvoices />}
                  />
                  <Route path="time-tracking" element={<TimeTracking />} />
                  <Route path="expenses" element={<Expenses />} />
                  <Route
                    path="client-notifications"
                    element={<ClientNotifications />}
                  />
                <Route path="settings" element={<UserSettings />} />
                {process.env.NODE_ENV === "development" && (
                  <Route path="devtools" element={<DevTools />} />
                )}
              </Route>
            </Route>

              <Route path="/logout" element={<LogoutRoute />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </LocaleProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}
